/**
 * Imperative confirm dialog that works inside the Office add-in webview.
 *
 * `window.confirm()` is unreliable / blocked in several Office host webviews,
 * so delete buttons that gated on it silently did nothing. This builds a small
 * modal directly on document.body and resolves a Promise<boolean>.
 *
 * Usage:
 *   if (!(await confirmDialog("Delete this?"))) return;
 */
export function confirmDialog(message, { confirmLabel = "Delete", cancelLabel = "Cancel", danger = true } = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("data-no-translate", "true");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:3000;" +
      "display:flex;align-items:center;justify-content:center;padding:20px;";

    const box = document.createElement("div");
    box.style.cssText =
      "background:#0f172a;border:1px solid #334155;border-radius:8px;padding:18px;" +
      "max-width:380px;width:100%;color:#e2e8f0;font-size:13px;" +
      "box-shadow:0 10px 40px rgba(0,0,0,0.5);";

    const msg = document.createElement("div");
    msg.textContent = message;
    msg.style.cssText = "margin-bottom:16px;line-height:1.4;";

    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:8px;justify-content:flex-end;";

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = cancelLabel;
    cancelBtn.style.cssText =
      "padding:6px 14px;border-radius:6px;border:1px solid #334155;" +
      "background:transparent;color:#e2e8f0;cursor:pointer;font-size:12px;";

    const okBtn = document.createElement("button");
    okBtn.textContent = confirmLabel;
    okBtn.style.cssText =
      "padding:6px 14px;border-radius:6px;border:none;cursor:pointer;font-size:12px;font-weight:600;" +
      (danger ? "background:#dc2626;color:#fff;" : "background:#0ea5e9;color:#fff;");

    let done = false;
    const cleanup = (val) => {
      if (done) return;
      done = true;
      document.removeEventListener("keydown", onKey);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      resolve(val);
    };
    const onKey = (e) => {
      if (e.key === "Escape") cleanup(false);
      if (e.key === "Enter") cleanup(true);
    };

    cancelBtn.onclick = () => cleanup(false);
    okBtn.onclick = () => cleanup(true);
    overlay.onclick = (e) => { if (e.target === overlay) cleanup(false); };
    document.addEventListener("keydown", onKey);

    row.appendChild(cancelBtn);
    row.appendChild(okBtn);
    box.appendChild(msg);
    box.appendChild(row);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    // Focus the confirm button so Enter works immediately
    setTimeout(() => okBtn.focus(), 0);
  });
}
